import * as THREE from "three";

/**
 * The kill effects' particle specs (views/killFx.ts): ONE reused spec object per particle kind, every field the sprite
 * VFX reads set here (art/vfx.js `spawn` copies the spec into a recycled particle), so a spawn never sees a stale field
 * and a frame allocates nothing. Colours linear RGB; sizes world units; lives seconds.
 */
type Spec = Record<string, unknown> & { pos: THREE.Vector3; vel: THREE.Vector3; life: number; rot: number; swirl: number };

const WHITE = [1, 1, 1] as const;
const GOLD = [1, 0.82, 0.32] as const, GOLD_HI = [1, 0.95, 0.7] as const;
const EMBER_CORE = [1, 0.62, 0.22] as const, EMBER_COOL = [0.8, 0.14, 0.03] as const, EMBER_GLOW = [1, 0.45, 0.12] as const;
/** A wisp starts as a dim ember glow and cools to a smoky violet-grey as it curls away. */
const WISP_WARM = [0.85, 0.3, 0.08] as const, WISP_COOL = [0.16, 0.11, 0.14] as const;

function spec(o: Record<string, unknown>): Spec {
  return {
    pos: new THREE.Vector3(), vel: new THREE.Vector3(), life: 1, gravity: 0, drag: 0, size0: 0.1, size1: 0.1, sizeEase: null, aspect: 1,
    rot: 0, rotVel: 0, color0: WHITE, color1: WHITE, alpha: 1, fadeIn: 0.05, fadeOut: 0.5, additive: true, stretch: 0, frame: "glow",
    anim: null, floor: -Infinity, bright: 1, flat: false, align: false, ramp: null, animRate: 0, swirl: 0, ...o,
  } as Spec;
}
const sinEase = (t: number): number => Math.sin(t * Math.PI);

/** When a coin's landing wink lights: this fraction into its life; dark (size 0) before it. */
export const PING_AT = 0.72;
const pingEase = (t: number): number => (t < PING_AT ? 0 : Math.sin(((t - PING_AT) / (1 - PING_AT)) * Math.PI));

/** Gravity on a thrown coin, m/s² (also its landing time below). */
const G = 9;

export const FLASH = spec({ frame: "glow", life: 0.28, size0: 0.4, size1: 1.0, color0: GOLD_HI, bright: 2.2, fadeIn: 0, fadeOut: 0.8 });
export const COIN = spec({ frame: "coin", life: 1.05, gravity: G, drag: 0.2, size0: 0.19, size1: 0.19, additive: false, fadeIn: 0, fadeOut: 0.2 });
export const SPARKLE = spec({ frame: "star", life: 0.8, size0: 0, size1: 0.22, sizeEase: sinEase, color0: GOLD, bright: 2.2, fadeIn: 0.1, fadeOut: 0.3 });
export const GLINT = spec({ frame: "star", life: 0.45, size0: 0, size1: 0.11, sizeEase: sinEase, color0: GOLD_HI, bright: 2.4, fadeIn: 0.1, fadeOut: 0.4 });
/** A coin's wink on the road where it lands: spawned with the coin, its life set so the wink falls on the landing. */
export const PING = spec({ frame: "star", life: 0.6, size0: 0, size1: 0.26, sizeEase: pingEase, color0: GOLD_HI, bright: 2.6, fadeIn: 0, fadeOut: 0.05 });
export const PUFF = spec({ frame: "glow", life: 0.4, size0: 0.3, size1: 0.75, color0: EMBER_GLOW, bright: 1.8, fadeIn: 0, fadeOut: 0.8 });
/** Ember Wisps' spat sparks at the death: fast, falling, streaked. */
export const SPARK = spec({ frame: "spark", life: 0.5, gravity: 7, size0: 0.07, size1: 0.03, color0: EMBER_CORE, color1: EMBER_GLOW, bright: 2.6, fadeIn: 0, fadeOut: 0.4, stretch: 0.05, align: true });
export const EMBER = spec({ frame: "glow", life: 1.2, drag: 0.35, size0: 0.08, size1: 0.025, color0: EMBER_CORE, color1: EMBER_COOL, bright: 1.7, fadeIn: 0.08, fadeOut: 0.5 });
/** A long faint streak along its own flight: as it spirals the streak turns with it, a curl of smoke behind the ember. */
export const WISP = spec({ frame: "glow", life: 1.4, drag: 0.5, size0: 0.2, size1: 0.5, aspect: 0.2, align: true, color0: WISP_WARM, color1: WISP_COOL, alpha: 0.75, bright: 1.5, fadeIn: 0.25, fadeOut: 0.55 });

export const rnd = (a: number, b: number): number => a + Math.random() * (b - a);

/**
 * Seconds until a coin thrown up at `vy` from `drop` above the road lands (no drag: the drag is small over a second, and
 * the wink only has to fall near the landing).
 */
export function coinLanding(vy: number, drop: number): number {
  return (vy + Math.sqrt(vy * vy + 2 * G * Math.max(0, drop))) / G;
}
